import { useQuery } from "@tanstack/react-query";
import { auth } from "../../components/Auth/Auth";

/**
 * Fetches WooCommerce orders from the backend.
 * The backend is responsible for accessing Cosmos DB.
 */
const GetWooCommerceOrdersAPI = async () => {
  const user = auth.currentUser;

  if (!user) {
    throw new Error("No authenticated user found");
  }

  // Gets the current Firebase ID token.
  const token = await user.getIdToken();

  const response = await fetch(
    `${import.meta.env.VITE_API_URL}/api/v1/woocommerce/orders`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    },
  );

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error("Unauthorized: Please log in again.");
    }

    const errorData = await response.json().catch(() => null);

    throw new Error(
      errorData?.message || "Failed to fetch WooCommerce orders",
    );
  }

  return response.json();
};

/**
 * React Query hook for WooCommerce orders.
 */
export const useWooCommerceOrders = () => {
  const { data, isPending, error } = useQuery({
    queryKey: ["woocommerceOrders"],
    queryFn: GetWooCommerceOrdersAPI,
    retry: false,
    refetchInterval: 10 * 60 * 1000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  return { data, isPending, error };
};